import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getDossierDocumentsByFileId } from '../../data/dossierDocumentsMock';
import { formatDossierDate } from '../../utils/dossierFormat';
import type { DossierDocument } from '../../types/dossierDocument.types';
import {
  $CarouselDot,
  $CarouselDots,
  $CarouselSlide,
  $CarouselTrack,
  $CarouselViewport,
  $DocumentCard,
  $DocumentFooter,
  $DocumentMeta,
  $DocumentName,
  $DocumentPreview,
  $EmptyState,
  $Section,
  $SectionCount,
  $SectionHeader,
  $SectionTitle,
} from './DossierDocumentsSection.styles';

export interface DossierDocumentsSectionProps {
  fileId: string;
}

const getDocumentAuthorLabel = (document: DossierDocument): string =>
  document.origin === 'generated' ? 'Système' : document.uploaderLabel;

const DossierDocumentsSection = ({ fileId }: DossierDocumentsSectionProps) => {
  const documents = useMemo(() => getDossierDocumentsByFileId(fileId), [fileId]);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  });

  useEffect(() => {
    setSelectedSlideIndex(0);
    emblaApi?.scrollTo(0, true);
  }, [emblaApi, fileId]);

  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }

    const onSelect = () => {
      setSelectedSlideIndex(emblaApi.selectedScrollSnap());
    };

    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const handleDotClick = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  if (documents.length === 0) {
    return (
      <$Section data-testid="dossier-documents-section">
        <$SectionHeader>
          <$SectionTitle>Documents</$SectionTitle>
        </$SectionHeader>
        <$EmptyState>Aucun document disponible pour ce dossier.</$EmptyState>
      </$Section>
    );
  }

  return (
    <$Section data-testid="dossier-documents-section">
      <$SectionHeader>
        <$SectionTitle>Documents</$SectionTitle>
        <$SectionCount>
          {documents.length} document{documents.length > 1 ? 's' : ''}
        </$SectionCount>
      </$SectionHeader>

      <$CarouselViewport ref={emblaRef}>
        <$CarouselTrack>
          {documents.map((document) => (
            <$CarouselSlide key={document.id}>
              <$DocumentCard>
                <$DocumentPreview $extension={document.extension}>
                  {document.extension}
                </$DocumentPreview>
                <$DocumentMeta>
                  <$DocumentName>{document.name}</$DocumentName>
                </$DocumentMeta>
                <$DocumentFooter>
                  <span>{formatDossierDate(document.uploadDate)}</span>
                  <span>{getDocumentAuthorLabel(document)}</span>
                </$DocumentFooter>
              </$DocumentCard>
            </$CarouselSlide>
          ))}
        </$CarouselTrack>
      </$CarouselViewport>

      {documents.length > 1 ? (
        <$CarouselDots>
          {documents.map((document, index) => (
            <$CarouselDot
              key={document.id}
              type="button"
              $isActive={selectedSlideIndex === index}
              aria-label={`Afficher le document ${index + 1}`}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </$CarouselDots>
      ) : null}
    </$Section>
  );
};

export default DossierDocumentsSection;
